import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, ExternalLink, Reply, Loader2, Paperclip, Star, CheckSquare, Check, Square, MoreHorizontal, Download, FileText, Image, File, ChevronDown, ChevronUp, Brain, Sparkles, Clock, Eye } from 'lucide-react';
import { mailApi, todoApi, aiApi, MailTodoLink } from '../../api/client';
import { formatDateTime } from '../../utils/date';
import { AddToTodoModal } from '../board/AddToTodoModal';
import { SubtaskList } from '../board/SubtaskList';
import AiReplyDraft from './AiReplyDraft';
import SnoozeModal from './SnoozeModal';
import { toast } from 'sonner';
import { useAuthStore } from '../../stores/auth.store';

interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return Image;
  if (contentType.includes('pdf') || contentType.includes('document')) return FileText;
  return File;
}

interface ReadingPaneProps {
  accountId: string;
  messageId: string;
  provider: string;
  onClose: () => void;
  onQuickAction?: (mailData: { message: any; accountId: string }) => void;
}

interface Message {
  id: string;
  subject: string;
  from: { name: string; email: string };
  to?: Array<{ name: string; email: string }>;
  cc?: Array<{ name: string; email: string }>;
  receivedAt: string;
  isRead: boolean;
  hasAttachments: boolean;
  importance: string;
  categories?: string[];
  bodyPreview: string;
  body?: string;
}

export default function ReadingPane({ accountId, messageId, provider, onClose, onQuickAction }: ReadingPaneProps) {
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showAiInsights, setShowAiInsights] = useState(false);
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; name: string } | null>(null);
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['message', accountId, messageId],
    queryFn: () => mailApi.getMessage(accountId, messageId),
  });
  
  const { data: attachmentsData, isLoading: attachmentsLoading } = useQuery({
    queryKey: ['attachments', accountId, messageId],
    queryFn: () => mailApi.getAttachments(accountId, messageId),
    enabled: showAttachments,
  });
  
  const attachments = attachmentsData?.attachments || [];
  
  const { data: todoLinkData } = useQuery({
    queryKey: ['mailTodoLink', accountId, messageId],
    queryFn: () => todoApi.getMailLink(accountId, messageId),
    staleTime: 30 * 1000,
  });
  
  const todoLink = todoLinkData?.link;
  
  const { data: aiEvalsData } = useQuery({
    queryKey: ['messageAiEvaluations', accountId, messageId],
    queryFn: () => aiApi.getMessageEvaluations(accountId, messageId),
    staleTime: 60 * 1000,
  });
  
  const aiEvaluations = aiEvalsData?.evaluations?.filter(e => e.matches) || [];
  
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['mailSummary', accountId, messageId],
    queryFn: async () => {
      const cached = await mailApi.getSummaries(accountId, [messageId]);
      if (cached.summaries[messageId]) {
        return { summary: cached.summaries[messageId] };
      }
      const msg = data?.message as Message | undefined;
      if (!msg) return { summary: null };
      const result = await mailApi.generateSummary(accountId, {
        messageId,
        subject: msg.subject || '',
        bodyPreview: msg.bodyPreview || '',
        from: msg.from?.name || msg.from?.email || '',
      });
      return { summary: result.summary };
    },
    enabled: !!data?.message,
    staleTime: 5 * 60 * 1000,
  });
  
  const summary = summaryData?.summary;
  
  const toggleMutation = useMutation({
    mutationFn: () => todoApi.toggleMailTodo(accountId, messageId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mailTodoLink', accountId, messageId] });
      queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
      toast.success(data.message);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren der Aufgabe');
    },
  });
  
  const message = data?.message as Message | undefined;
  
  const handleOpenInOriginal = async () => {
    const { url } = await mailApi.getOpenUrl(accountId, messageId);
    window.open(url, '_blank');
  };
  
  const handleReply = async () => {
    const { url } = await mailApi.getReplyUrl(accountId, messageId);
    window.open(url, '_blank');
  };
  
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error || !message) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <p style={{ color: 'var(--text-tertiary)' }}>Nachricht konnte nicht geladen werden</p>
      </div>
    );
  }
  
  return (
    <>
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Mini Sticky Header - nur Absender, Datum, Buttons */}
      <div 
        className="flex-shrink-0 px-3 py-2 flex items-center gap-2"
        style={{ borderBottom: '1px solid var(--border-default)' }}
      >
        <div className="flex-1 min-w-0 flex items-center gap-2 text-sm">
          <span 
            className="font-medium truncate"
            style={{ color: 'var(--text-primary)' }}
          >
            {message.from.name || message.from.email}
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <span className="flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
            {formatDateTime(message.receivedAt)}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onQuickAction && message && (
            <button
              onClick={() => onQuickAction({ message, accountId })}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Aktionen..."
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Großer scrollbarer Bereich - ALLES außer Mini-Header */}
      <div className="flex-1 overflow-y-auto">
        {/* Betreff */}
        <div className="px-4 pt-3">
          <h2 
            className="text-lg font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            {message.subject}
          </h2>
        </div>

        {/* Recipients */}
        {(message.to?.length || message.cc?.length) ? (
          <div className="px-4 pt-2">
            {message.to && message.to.length > 0 && (
              <div className="text-sm">
                <span style={{ color: 'var(--text-tertiary)' }}>An:</span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>
                  {message.to.map((r) => r.name || r.email).join(', ')}
                </span>
              </div>
            )}
            {message.cc && message.cc.length > 0 && (
              <div className="text-sm">
                <span style={{ color: 'var(--text-tertiary)' }}>CC:</span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>
                  {message.cc.map((r) => r.name || r.email).join(', ')}
                </span>
              </div>
            )}
          </div>
        ) : null}
        
        {/* Meta badges */}
        <div className="px-4 pt-2 flex flex-wrap gap-2">
          {message.importance === 'high' && (
            <span 
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded font-medium"
              style={{
                backgroundColor: 'var(--color-error-bg)',
                color: 'var(--color-error)',
              }}
            >
              <Star className="w-3 h-3 fill-current" />
              Wichtig
            </span>
          )}
          {message.hasAttachments && (
            <button
              onClick={() => setShowAttachments(!showAttachments)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer transition-colors"
              style={{
                backgroundColor: showAttachments ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-secondary)',
                color: showAttachments ? 'var(--brand-primary)' : 'var(--text-secondary)',
                border: showAttachments ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent',
              }}
            >
              <Paperclip className="w-3 h-3" />
              Anhänge
              {showAttachments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          {message.categories?.map((cat, i) => (
            <span
              key={i}
              className="px-2 py-1 text-xs rounded font-medium"
              style={{
                backgroundColor: 'rgba(99, 102, 241, 0.15)',
                color: 'var(--brand-primary)',
              }}
            >
              {cat}
            </span>
          ))}
        </div>
        
        {/* AI Evaluations - Collapsible */}
        {aiEvaluations.length > 0 && (
          <div className="px-4 pt-2">
            <button
              onClick={() => setShowAiInsights(!showAiInsights)}
              className="flex items-center gap-2 w-full p-2 rounded-lg transition-colors"
              style={{ 
                backgroundColor: 'rgba(168, 85, 247, 0.08)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <Brain className="w-4 h-4 flex-shrink-0" style={{ color: 'rgb(168, 85, 247)' }} />
              <span 
                className="text-sm font-medium flex-1 text-left"
                style={{ color: 'var(--text-primary)' }}
              >
                KI-Analyse ({aiEvaluations.length})
              </span>
              <span
                className="px-2 py-0.5 text-xs rounded-full font-medium"
                style={{
                  backgroundColor: aiEvaluations[0]?.confidence >= 0.8 
                    ? 'rgba(34, 197, 94, 0.15)' 
                    : 'rgba(168, 85, 247, 0.15)',
                  color: aiEvaluations[0]?.confidence >= 0.8 
                    ? 'var(--color-success)' 
                    : 'rgb(168, 85, 247)',
                }}
              >
                {Math.round((aiEvaluations[0]?.confidence || 0) * 100)}%
              </span>
              {showAiInsights ? (
                <ChevronUp className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              ) : (
                <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              )}
            </button>
            
            {showAiInsights && (
              <div className="mt-2 space-y-2">
                {aiEvaluations.map((aiEval, idx) => (
                  <div
                    key={`${aiEval.columnId}-${idx}`}
                    className="p-3 rounded-lg"
                    style={{ 
                      backgroundColor: 'rgba(168, 85, 247, 0.05)',
                      border: '1px solid rgba(168, 85, 247, 0.15)',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span 
                        className="text-sm font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {aiEval.columnName}
                      </span>
                      <span
                        className="px-2 py-0.5 text-xs rounded-full font-medium"
                        style={{
                          backgroundColor: aiEval.confidence >= 0.8 
                            ? 'rgba(34, 197, 94, 0.15)' 
                            : aiEval.confidence >= 0.5
                              ? 'rgba(168, 85, 247, 0.15)'
                              : 'rgba(234, 179, 8, 0.15)',
                          color: aiEval.confidence >= 0.8 
                            ? 'var(--color-success)' 
                            : aiEval.confidence >= 0.5
                              ? 'rgb(168, 85, 247)'
                              : 'var(--color-warning)',
                        }}
                      >
                        {Math.round(aiEval.confidence * 100)}%
                      </span>
                    </div>
                    <div 
                      className="text-xs mb-1"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      Board: {(aiEval as any).boardName || 'Board'}
                    </div>
                    {aiEval.reasoning && (
                      <p 
                        className="text-sm italic"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        "{aiEval.reasoning}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Attachments List */}
        {showAttachments && (
          <div className="px-4 pt-2">
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              {attachmentsLoading ? (
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Anhänge werden geladen...
                </div>
              ) : attachments.length === 0 ? (
                <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  Keine Anhänge gefunden
                </div>
              ) : (
                <div className="space-y-2">
                  {attachments.filter(a => !a.isInline).map((attachment) => {
                    const FileIcon = getFileIcon(attachment.contentType);
                    const downloadUrl = mailApi.getAttachmentDownloadUrl(accountId, messageId, attachment.id);
                    
                    const isPdf = attachment.contentType === 'application/pdf' || attachment.name?.toLowerCase().endsWith('.pdf');
                    
                    const handleDownload = async (e?: React.MouseEvent) => {
                      e?.stopPropagation();
                      try {
                        const response = await fetch(downloadUrl, {
                          headers: { 'Authorization': `Bearer ${accessToken}` },
                        });
                        if (!response.ok) throw new Error('Download fehlgeschlagen');
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = attachment.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        toast.error('Download fehlgeschlagen');
                      }
                    };
                    
                    const handlePreviewPdf = async (e: React.MouseEvent) => {
                      e.stopPropagation();
                      try {
                        const response = await fetch(downloadUrl, {
                          headers: { 'Authorization': `Bearer ${accessToken}` },
                        });
                        if (!response.ok) throw new Error('Vorschau fehlgeschlagen');
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        setPdfPreview({ url, name: attachment.name });
                      } catch (error) {
                        toast.error('PDF-Vorschau fehlgeschlagen');
                      }
                    };
                    
                    return (
                      <div
                        key={attachment.id}
                        className="flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer w-full text-left"
                        style={{ backgroundColor: 'var(--bg-primary)' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
                        onClick={() => isPdf ? handlePreviewPdf({} as any) : handleDownload()}
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: isPdf ? 'rgba(239, 68, 68, 0.1)' : 'rgba(99, 102, 241, 0.1)' }}
                        >
                          <FileIcon className="w-5 h-5" style={{ color: isPdf ? 'var(--color-error)' : 'var(--brand-primary)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div 
                            className="text-sm font-medium truncate"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {attachment.name}
                          </div>
                          <div 
                            className="text-xs"
                            style={{ color: 'var(--text-tertiary)' }}
                          >
                            {formatFileSize(attachment.size)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isPdf && (
                            <button
                              onClick={handlePreviewPdf}
                              className="p-1.5 rounded-lg transition-colors"
                              style={{ color: 'var(--text-tertiary)' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                              title="PDF-Vorschau"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDownload(e)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: 'var(--text-tertiary)' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            title="Herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div 
          className="px-4 pt-3 pb-2 flex flex-wrap gap-2"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <button
            onClick={handleOpenInOriginal}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-sm rounded-lg hover:opacity-90 transition-opacity font-medium flex-1 sm:flex-none justify-center min-w-0"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            }}
          >
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Original</span>
          </button>
          <button
            onClick={handleReply}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors font-medium flex-1 sm:flex-none justify-center min-w-0"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-primary)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              e.currentTarget.style.borderColor = 'var(--border-hover)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              e.currentTarget.style.borderColor = 'var(--border-default)';
            }}
          >
            <Reply className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Antworten</span>
          </button>
          <button
            onClick={() => setShowAiDraft(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors font-medium flex-1 sm:flex-none justify-center min-w-0"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.15))',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              color: 'var(--brand-primary)',
            }}
            title="AI Antwort-Entwurf generieren"
          >
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">AI Entwurf</span>
          </button>
          <button
            onClick={() => setShowSnoozeModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors font-medium flex-1 sm:flex-none justify-center min-w-0"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-primary)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            title="E-Mail zurückstellen"
          >
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Snooze</span>
          </button>
          {todoLink ? (
            <button
              onClick={() => toggleMutation.mutate()}
              disabled={toggleMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors font-medium flex-1 sm:flex-none justify-center min-w-0"
              style={{
                backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                border: `1px solid ${todoLink.completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
                color: todoLink.completed ? 'var(--color-success)' : 'var(--brand-primary)',
              }}
              title={todoLink.completed ? 'Als unerledigt markieren' : 'Als erledigt markieren'}
            >
              {toggleMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              ) : todoLink.completed ? (
                <Check className="w-4 h-4 flex-shrink-0" />
              ) : (
                <Square className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="truncate">{todoLink.completed ? 'Erledigt' : 'Aufgabe'}</span>
            </button>
          ) : (
            <button
              onClick={() => setShowTodoModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors font-medium flex-1 sm:flex-none justify-center min-w-0"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                e.currentTarget.style.borderColor = 'var(--border-hover)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-default)';
              }}
              title={`Zu ${provider === 'microsoft' ? 'Microsoft To-Do' : 'Google Tasks'} hinzufügen`}
            >
              <CheckSquare className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">To-Do</span>
            </button>
          )}
        </div>

        {/* AI Summary */}
        {(summary || summaryLoading) && (
          <div className="px-4 pt-3">
            <div 
              className="p-3 rounded-lg"
              style={{ 
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(99, 102, 241, 0.08))',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4" style={{ color: 'rgb(168, 85, 247)' }} />
                <span className="text-xs font-medium" style={{ color: 'rgb(168, 85, 247)' }}>
                  KI-Zusammenfassung
                </span>
              </div>
              {summaryLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                  <span className="text-sm italic" style={{ color: 'var(--text-tertiary)' }}>
                    Wird generiert...
                  </span>
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  {summary}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Microsoft/Google To-Do Subtasks */}
        {todoLink && provider === 'microsoft' && (
          <div className="px-4 pt-3">
            <div 
              className="p-3 rounded-lg"
              style={{ 
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-default)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckSquare className="w-4 h-4" style={{ color: 'var(--brand-primary)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                  Microsoft To-Do Subtasks
                </span>
              </div>
              <SubtaskList 
                accountId={accountId} 
                messageId={messageId}
                provider={provider}
                todoLinkExists={!!todoLink}
              />
            </div>
          </div>
        )}
        
        {/* Body */}
        <div 
          className="px-4 py-4"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'A' || target.closest('a')) {
              e.preventDefault();
              const link = (target.tagName === 'A' ? target : target.closest('a')) as HTMLAnchorElement;
              if (link?.href) {
                window.open(link.href, '_blank', 'noopener,noreferrer');
              }
            }
          }}
        >
          {message.body ? (
            <div 
              className="prose-email max-w-none"
              dangerouslySetInnerHTML={{ __html: message.body }}
            />
          ) : (
            <p 
              className="whitespace-pre-wrap"
              style={{ color: 'var(--text-primary)' }}
            >
              {message.bodyPreview}
            </p>
          )}
        </div>
      </div>
    </div>

    {/* Modals - outside scroll structure */}
    {message && (
      <AddToTodoModal
        isOpen={showTodoModal}
        onClose={() => {
          setShowTodoModal(false);
          queryClient.invalidateQueries({ queryKey: ['mailTodoLink', accountId, messageId] });
          queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
        }}
        accountId={accountId}
        messageId={messageId}
        mailSubject={message.subject}
        mailFrom={message.from.name || message.from.email}
        provider={provider as 'microsoft' | 'google'}
      />
    )}

    {showAiDraft && (
      <AiReplyDraft
        accountId={accountId}
        messageId={messageId}
        onClose={() => setShowAiDraft(false)}
        onUse={(content, subject) => {
          navigator.clipboard.writeText(content);
          toast.success('Entwurf kopiert - öffne Antwort-Fenster');
          handleReply();
        }}
      />
    )}

    {showSnoozeModal && (
      <SnoozeModal
        accountId={accountId}
        messageId={messageId}
        onClose={() => setShowSnoozeModal(false)}
        onSnoozed={() => {
          queryClient.invalidateQueries({ queryKey: ['messages'] });
        }}
      />
    )}
    
    {/* PDF Preview Modal */}
    {pdfPreview && (
      <div 
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
        onClick={() => {
          window.URL.revokeObjectURL(pdfPreview.url);
          setPdfPreview(null);
        }}
      >
        <div 
          className="w-[90vw] h-[90vh] rounded-xl overflow-hidden flex flex-col shadow-2xl"
          style={{ backgroundColor: 'var(--bg-primary)' }}
          onClick={e => e.stopPropagation()}
        >
          <div 
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border-default)', backgroundColor: 'var(--bg-secondary)' }}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: 'var(--color-error)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {pdfPreview.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={pdfPreview.url}
                download={pdfPreview.name}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Herunterladen"
              >
                <Download className="w-4 h-4" />
              </a>
              <button 
                onClick={() => {
                  window.URL.revokeObjectURL(pdfPreview.url);
                  setPdfPreview(null);
                }}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <iframe
            src={pdfPreview.url}
            className="flex-1 w-full"
            title={pdfPreview.name}
          />
        </div>
      </div>
    )}
    </>
  );
}
